import { create } from 'zustand';
import { getAvailableParameters } from '../api/revit/revitBridge';
import type { ParameterSource } from '../api/grpc/gen/family_service_pb';
import type { RevitOperationProgressSnapshot } from '../api/grpc/operationProgressClient';
import { normalizeMappingProjectKey } from '../lib/mappingProjectIdentity';
import { registerRevitModelCacheInvalidator } from '../lib/revit/modelCacheInvalidation';
import { RevitCatalogInspectionTimeoutMs } from '../lib/revit/revitOperationTimeoutPolicy';

interface RevitParameter {
  name: string;
  label: string;
  parameterType: 'Type' | 'Instance';
  group: string;
  dataType: string;
  isProjectBinding?: boolean;
  categories?: string[];
  source?: ParameterSource;
}

export interface RevitParametersFetchContext {
  projectKey?: string | null;
  modelVersion?: number | null;
  force?: boolean;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

interface RevitParametersState {
  parameters: RevitParameter[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  projectKey: string | null;
  modelVersion: number | null;
  fetchParameters: (context?: RevitParametersFetchContext) => Promise<void>;
}

let fetchSequence = 0;

function normalizeModelVersion(modelVersion: number | null | undefined): number | null {
  return typeof modelVersion === 'number' && Number.isFinite(modelVersion)
    ? modelVersion
    : null;
}

function isSameFetchScope(
  state: Pick<RevitParametersState, 'projectKey' | 'modelVersion'>,
  projectKey: string | null,
  modelVersion: number | null
): boolean {
  return state.projectKey === projectKey && state.modelVersion === modelVersion;
}

function withParameterCatalogTimeout<T>(promise: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = globalThis.setTimeout(() => {
      reject(new Error('Timed out while inspecting active Revit project parameters.'));
    }, RevitCatalogInspectionTimeoutMs);

    promise
      .then((value) => {
        globalThis.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        globalThis.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

/**
 * Store for caching Revit parameters available in pipe-related categories
 * Fetches parameters once and caches them for the session
 */
export const useRevitParametersStore = create<RevitParametersState>((set, get) => ({
  parameters: [],
  isLoading: false,
  isLoaded: false,
  error: null,
  projectKey: null,
  modelVersion: null,

  fetchParameters: async (context = {}) => {
    const projectKey = normalizeMappingProjectKey(context.projectKey);
    const modelVersion = normalizeModelVersion(context.modelVersion);
    const state = get();
    const sameScope = isSameFetchScope(state, projectKey, modelVersion);

    // Don't fetch if already loading or loaded for the same active project scope.
    if (!context.force && sameScope && (state.isLoading || state.isLoaded)) {
      return;
    }

    const fetchId = ++fetchSequence;
    set({
      parameters: sameScope ? state.parameters : [],
      isLoading: true,
      isLoaded: false,
      error: null,
      projectKey,
      modelVersion,
    });

    try {
      const response = await withParameterCatalogTimeout(
        getAvailableParameters({
          onProgress: context.onProgress,
        })
      );
      if (fetchId !== fetchSequence) {
        return;
      }

      if (response.success && response.data) {
        set({
          parameters: response.data,
          isLoading: false,
          isLoaded: true,
          error: null,
          projectKey,
          modelVersion,
        });
      } else {
        const errorMsg = response.error || 'Failed to fetch parameters';
        console.error('[useRevitParametersStore] Error:', errorMsg);
        set({
          parameters: [],
          isLoading: false,
          isLoaded: false,
          error: errorMsg,
          projectKey,
          modelVersion,
        });
      }
    } catch (error) {
      if (fetchId !== fetchSequence) {
        return;
      }
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('[useRevitParametersStore] Exception:', errorMsg);
      set({
        parameters: [],
        isLoading: false,
        isLoaded: false,
        error: errorMsg,
        projectKey,
        modelVersion,
      });
    }
  },
}));

export function invalidateRevitParametersStore(): void {
  fetchSequence += 1;
  useRevitParametersStore.setState({
    parameters: [],
    isLoading: false,
    isLoaded: false,
    error: null,
    projectKey: null,
    modelVersion: null,
  });
}

registerRevitModelCacheInvalidator((event) => {
  if (
    event.eventType === 'ModelChanged' ||
    event.eventType === 'DocumentOpened' ||
    event.eventType === 'DocumentChanged' ||
    event.eventType === 'DocumentClosed'
  ) {
    invalidateRevitParametersStore();
  }
});
